"use client";

// Инъекция кнопок: компоненты пакета не зависят от конкретной кнопочной
// реализации потребителя. По умолчанию — минимально стилизованный нативный
// <button> на семантических токенах (см. README «Tailwind-контракт»);
// потребитель с дизайн-системной кнопкой передаёт свой renderAction.
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ActionVariant = "primary" | "secondary" | "ghost" | "danger";
export type ActionSize = "sm" | "md";

export interface RenderActionProps {
  variant: ActionVariant;
  size?: ActionSize | undefined;
  onClick: () => void;
  disabled?: boolean | undefined;
  children: ReactNode;
  "aria-label"?: string | undefined;
  "data-testid"?: string | undefined;
}

export type RenderAction = (props: RenderActionProps) => ReactNode;

const VARIANT_CLASSES: Record<ActionVariant, string> = {
  secondary: "border border-border bg-surface text-text-primary hover:bg-border",
  ghost: "border border-border bg-background text-text-primary hover:bg-surface",
  primary: "border border-primary bg-primary text-on-primary hover:bg-primary-hover",
  danger: "border border-danger bg-danger text-on-danger hover:bg-danger-dark",
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: ActionVariant | undefined;
  size?: ActionSize | undefined;
}

/** Shared native button implementation for the public Button and legacy renderAction API. */
export function Button({
  variant = "primary",
  size = "md",
  type = "button",
  className,
  children,
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-[length:var(--ui-feedback-focus-ring-width)] focus-visible:ring-focus-ring focus-visible:ring-offset-[var(--ui-feedback-focus-ring-offset)] disabled:cursor-not-allowed disabled:opacity-[var(--ui-feedback-disabled-opacity)] ${VARIANT_CLASSES[variant]} ${className ?? ""}`}
      style={{
        ...style,
        minHeight: `max(var(--ui-minimum-control-height), var(--ui-button-${size}-min-height))`,
        borderRadius: "var(--ui-button-radius)",
        paddingInline: `var(--ui-button-${size}-padding-x)`,
        paddingBlock: `var(--ui-button-${size}-padding-y)`,
        fontSize: `var(--ui-button-${size}-font-size)`,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Нативный <button> на семантических токенах — дефолт без дизайн-системы потребителя. */
export const renderDefaultAction: RenderAction = ({ variant, size, ...props }) => (
  <Button variant={variant} size={size} {...props} />
);
